import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Presets de cenario (espelham os ids/labels de
// supabase/functions/_shared/gemini-image.ts - o texto do prompt fica so no
// servidor, o painel so precisa do id pra enviar e do label pra exibir).
// ---------------------------------------------------------------------------

export interface ScenePresetOption {
  readonly id: string;
  readonly label: string;
}

export const SCENE_PRESET_OPTIONS: readonly ScenePresetOption[] = [
  { id: "mesa-cafe", label: "Mesa de café da manhã" },
  { id: "estudio-minimalista", label: "Estúdio minimalista" },
  { id: "ambiente-urbano", label: "Ambiente urbano" },
  { id: "sala-aconchegante", label: "Sala aconchegante" },
  { id: "natureza", label: "Ao ar livre / natureza" },
];

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

// ---------------------------------------------------------------------------
// useAiImageUsage: contador de cota (badge "X/50 este mes" ou "X/5 vitalicio")
// ---------------------------------------------------------------------------

export interface AiImageUsage {
  lifetimeUsed: number;
  monthUsed: number;
  monthCycle: string;
}

async function fetchAiImageUsage(): Promise<AiImageUsage | null> {
  const { data, error } = await supabase
    .from("ai_image_usage")
    .select("lifetime_used, month_used, month_cycle")
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    lifetimeUsed: data.lifetime_used,
    monthUsed: data.month_used,
    monthCycle: data.month_cycle,
  };
}

export function useAiImageUsage() {
  return useQuery<AiImageUsage | null, Error>({
    queryKey: ["ai-image-usage"],
    queryFn: fetchAiImageUsage,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// useAiImageHistory: historico de geracoes (galeria)
// ---------------------------------------------------------------------------

export interface AiImageHistoryItem {
  id: string;
  productUrl: string | null;
  sceneLabel: string;
  variantIndex: 0 | 1;
  storagePath: string;
  status: string;
  createdAt: string;
}

async function fetchAiImageHistory(limit: number): Promise<AiImageHistoryItem[]> {
  const { data, error } = await supabase
    .from("ai_images")
    .select("id, product_url, scene_prompt_label, variant_index, storage_path, watermarked_path, status, created_at")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;

  return (data ?? []).map((row) => ({
    id: row.id,
    productUrl: row.product_url,
    sceneLabel: row.scene_prompt_label,
    variantIndex: row.variant_index,
    // Versao com marca d'agua (aplicada pelo bot depois da geracao) quando existir.
    storagePath: row.watermarked_path ?? row.storage_path,
    status: row.status,
    createdAt: row.created_at,
  }));
}

export function useAiImageHistory(limit = 20) {
  return useQuery<AiImageHistoryItem[], Error>({
    queryKey: ["ai-image-history", limit],
    queryFn: () => fetchAiImageHistory(limit),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/** Gera uma signed URL sob demanda pra exibir a miniatura de um item do
 *  historico. Respeita a RLS do bucket privado ia-images (cliente
 *  autenticado, sem service_role). */
export async function getAiImageSignedUrl(storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("ia-images")
    .createSignedUrl(storagePath, 60 * 60);
  if (error) return null;
  return data?.signedUrl ?? null;
}

// ---------------------------------------------------------------------------
// Upload de imagem de origem/referencia (painel sobe antes de gerar)
// ---------------------------------------------------------------------------

export class InvalidUploadError extends Error {}

function validateUploadFile(file: File): void {
  if (!ALLOWED_UPLOAD_TYPES.has(file.type)) {
    throw new InvalidUploadError("Envie uma imagem JPG, PNG ou WEBP.");
  }
  if (file.size === 0 || file.size > MAX_UPLOAD_BYTES) {
    throw new InvalidUploadError("A imagem deve ter no máximo 8MB.");
  }
}

function extensionFor(file: File): string {
  if (file.type === "image/png") return "png";
  if (file.type === "image/webp") return "webp";
  return "jpg";
}

/** Sobe um arquivo pro path <tenant_id>/uploads/<uuid>.<ext> do bucket
 *  ia-images. Valida tipo/tamanho no cliente ANTES de subir (a Edge Function
 *  ai-image revalida no servidor, nunca confia so no client). */
async function uploadSourceImage(tenantId: string, file: File): Promise<string> {
  validateUploadFile(file);
  const path = `${tenantId}/uploads/${crypto.randomUUID()}.${extensionFor(file)}`;
  const { error } = await supabase.storage
    .from("ia-images")
    .upload(path, file, { contentType: file.type, upsert: false });
  if (error) {
    throw new InvalidUploadError("Não foi possível enviar a imagem. Tente novamente.");
  }
  return path;
}

// ---------------------------------------------------------------------------
// useGenerateAiImage: mutation principal (upload opcional + chamada da Edge Function)
// ---------------------------------------------------------------------------

export interface GeneratedImageVariant {
  variantIndex: 0 | 1;
  aspectRatio: "9:16" | "1:1";
  label: "story" | "feed";
  signedUrl: string | null;
}

export interface GenerateAiImageResult {
  images: GeneratedImageVariant[];
  partial: boolean;
  usage: { used: number; limit: number; source: "lifetime" | "monthly" };
}

export interface GenerateAiImageInput {
  tenantId: string;
  productUrl?: string;
  sourceImageUrl?: string;
  sourceImageFile?: File;
  referenceImageFile?: File;
  scenePresetId?: string;
  customPrompt?: string;
}

interface InvokeSuccess {
  data: GenerateAiImageResult;
}

interface InvokeErrorBody {
  error?: string;
  usage?: { used: number; limit: number; source: string };
}

const ERROR_LABELS: Record<string, string> = {
  invalid_source_image: "Envie uma imagem de produto válida (URL ou arquivo JPG/PNG/WEBP até 8MB).",
  invalid_scene_preset: "Escolha um cenário ou descreva o ambiente desejado.",
  tenant_not_found: "Tenant ativo não encontrado.",
  quota_exceeded: "Você atingiu o limite de imagens IA do seu plano.",
  platform_key_missing: "Geração de imagem temporariamente indisponível. Tente novamente em instantes.",
  gemini_rate_limited: "O Gemini limitou as chamadas agora. Tente novamente em alguns minutos.",
  gemini_upstream_error: "Erro ao gerar a imagem agora. Tente novamente em instantes.",
  quota_check_failed: "Não foi possível checar sua cota agora. Tente novamente.",
};

const DEFAULT_ERROR = "Não foi possível gerar a imagem agora. Tente novamente.";

async function readInvokeError(invokeError: unknown): Promise<{ code: string | null; usage?: InvokeErrorBody["usage"] }> {
  if (!(invokeError instanceof FunctionsHttpError)) {
    return { code: null };
  }
  try {
    const body = (await invokeError.context.json()) as InvokeErrorBody;
    return { code: body.error ?? null, usage: body.usage };
  } catch {
    return { code: null };
  }
}

export interface UseGenerateAiImageResult {
  generate: (input: GenerateAiImageInput) => Promise<GenerateAiImageResult | null>;
  loading: boolean;
  error: string | null;
}

/** Hook de geracao de imagem IA lifestyle (Modulo 3 de paridade). Sobe as
 *  imagens de origem/referencia opcionais ao Storage (se fornecidas como
 *  File), depois chama a Edge Function ai-image, que reclama a cota, gera as
 *  2 variacoes (story 9:16, feed 1:1) e devolve signed URLs. */
export function useGenerateAiImage(): UseGenerateAiImageResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const generate = useCallback(
    async (input: GenerateAiImageInput): Promise<GenerateAiImageResult | null> => {
      setLoading(true);
      setError(null);

      try {
        let sourceImagePath: string | undefined;
        let referenceImagePath: string | undefined;

        if (input.sourceImageFile) {
          sourceImagePath = await uploadSourceImage(input.tenantId, input.sourceImageFile);
        }
        if (input.referenceImageFile) {
          referenceImagePath = await uploadSourceImage(input.tenantId, input.referenceImageFile);
        }

        const { data, error: invokeError } = await supabase.functions.invoke<InvokeSuccess>("ai-image", {
          body: {
            productUrl: input.productUrl,
            sourceImageUrl: sourceImagePath ? undefined : input.sourceImageUrl,
            sourceImagePath,
            referenceImagePath,
            scenePresetId: input.scenePresetId,
            customPrompt: input.customPrompt,
          },
        });

        if (invokeError || !data?.data) {
          const { code, usage } = await readInvokeError(invokeError);
          const label = (code && ERROR_LABELS[code]) || DEFAULT_ERROR;
          setError(
            code === "quota_exceeded" && usage
              ? `${label} (${usage.used}/${usage.limit})`
              : label,
          );
          return null;
        }

        void queryClient.invalidateQueries({ queryKey: ["ai-image-usage"] });
        void queryClient.invalidateQueries({ queryKey: ["ai-image-history"] });

        return data.data;
      } catch (uploadError) {
        setError(
          uploadError instanceof InvalidUploadError
            ? uploadError.message
            : "Não foi possível enviar a imagem. Tente novamente.",
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [queryClient],
  );

  return { generate, loading, error };
}
